import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, makeStyles, Typography } from '@material-ui/core';
import i18n from 'i18n-js';

import { theme } from 'config/theme';
import commonStyles from 'config/commonStyles';
import tableMapsStore from 'store/tableMapsStore';
import { errorMessage, QrConflictError } from 'store/tableMapsStore/actionCreators';
import globalStore from 'store/globalStore';
import { TableMapDetails } from 'store/tableMapsStore/reducer';
import { ApplicationState } from 'store';
import { PopupConfirmation } from 'components/shared';
import { TableMapDraft, TableMapWizardProvider, useTableMapWizard } from './context';
import WizardStepper from './components/WizardStepper';
import BasicInformationStep from './steps/BasicInformationStep';
import MapConfigurationStep from './steps/MapConfigurationStep';
import TablePlacementStep from './steps/TablePlacementStep';
import TableDetailsStep from './steps/TableDetailsStep';
import ReviewStep, { isReviewReady } from './steps/ReviewStep';
import CreatedStep, { CreatedResult } from './steps/CreatedStep';

const TOTAL_STEPS = 6;

const useStyles = makeStyles({
    ...commonStyles,
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        backgroundColor: theme.palette.common.white,
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        padding: '6px 16px 0',
        backgroundColor: theme.palette.common.white,
        paddingBottom: 16,
        borderBottom: `1px solid ${theme.palette.grey['300']}`,
    },
    headerTopRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 12,
    },
    headerTitle: {
        fontSize: 18,
        fontWeight: 600,
        color: theme.palette.primary.dark,
        lineHeight: 1.2,
    },
    headerSubtitle: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        marginTop: 2,
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    body: {
        flex: 1,
        overflow: 'auto',
        padding: '24px 16px',
    },
    backButton: {
        color: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        fontWeight: 600,
        letterSpacing: 1.25,
    },
    cancelButton: {
        color: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        fontWeight: 600,
        letterSpacing: 1.25,
    },
    nextButton: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        fontWeight: 600,
        letterSpacing: 1.25,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
        '&:disabled': {
            backgroundColor: theme.palette.secondary.light,
            color: theme.palette.secondary.main,
        },
    },
    loadingRoot: {
        display: 'flex',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.common.white,
    },
});

function isStepValid(stepIndex: number, draft: TableMapDraft): boolean {
    if (stepIndex === 0) {
        return draft.name.trim() !== '';
    }
    if (stepIndex === 3) {
        return draft.tables.length > 0 && draft.tables.every((t) => !t.isActive || !!t.qrCode);
    }
    return true;
}

interface WizardShellProps {
    isEdit: boolean;
    editId?: string;
    originalDetails?: TableMapDetails;
}

function WizardShell({ isEdit, editId, originalDetails }: WizardShellProps) {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useDispatch();
    const { draft } = useTableMapWizard();
    const userProfile = useSelector((state: ApplicationState) => state.user.profile);
    const [stepIndex, setStepIndex] = useState(0);
    const [saving, setSaving] = useState(false);
    const [createdResult, setCreatedResult] = useState<CreatedResult | null>(null);
    const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

    const currentStep = stepIndex + 1;
    const canAdvance = isStepValid(stepIndex, draft);

    function handleNext() {
        if (stepIndex < TOTAL_STEPS - 1 && canAdvance) {
            setStepIndex((prev) => prev + 1);
        }
    }

    function handleBack() {
        if (stepIndex > 0) {
            setStepIndex((prev) => prev - 1);
        }
    }

    function handleCancel() {
        setCancelConfirmOpen(true);
    }

    function handleCancelConfirmed() {
        history.push('/table-maps');
    }

    function showSaveError(err: unknown, fallback: string) {
        const message =
            err instanceof QrConflictError
                ? i18n.t('tableMaps.wizard.review.qrConflict', {
                      tables: err.qrConflictLabels.join(', '),
                  })
                : errorMessage(err) ?? fallback;
        dispatch(globalStore.actionCreators.showToaster('error', message));
    }

    async function handleCreate() {
        setSaving(true);
        try {
            const createdAt = new Date().toISOString();
            const nameParts = [userProfile.firstName, userProfile.lastName].filter(Boolean);
            const createdBy =
                nameParts.length > 0
                    ? nameParts.join(' ')
                    : userProfile.email || i18n.t('tableMaps.wizard.created.unknownUser');

            const thunk = tableMapsStore.actionCreators.createTableMap({
                name: draft.name,
                isActive: draft.isActive,
                entrance: draft.entrance,
                size: draft.size,
                area: draft.area,
                tables: draft.tables,
                gridEnabled: draft.gridEnabled,
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const createdId = await (dispatch(thunk as any) as Promise<string>);

            setCreatedResult({
                id: createdId,
                name: draft.name,
                tablesCount: draft.tables.length,
                totalSeats: draft.tables.reduce((sum, t) => sum + t.seats, 0),
                status: draft.isActive ? 'Active' : 'Inactive',
                isActive: draft.isActive,
                createdAt,
                createdBy,
            });
            setStepIndex(5);
        } catch (err) {
            showSaveError(err, i18n.t('tableMaps.wizard.review.createError'));
        } finally {
            setSaving(false);
        }
    }

    async function handleUpdate() {
        if (!editId || !originalDetails) return;
        setSaving(true);
        try {
            const updatedAt = new Date().toISOString();
            const nameParts = [userProfile.firstName, userProfile.lastName].filter(Boolean);
            const createdBy =
                nameParts.length > 0
                    ? nameParts.join(' ')
                    : userProfile.email || i18n.t('tableMaps.wizard.created.unknownUser');

            const thunk = tableMapsStore.actionCreators.updateTableMap({
                id: editId,
                name: draft.name,
                isActive: draft.isActive,
                entrance: draft.entrance,
                size: draft.size,
                area: draft.area,
                tables: draft.tables,
                gridEnabled: draft.gridEnabled,
                originalTables: originalDetails.tables,
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            await (dispatch(thunk as any) as Promise<void>);

            setCreatedResult({
                id: editId,
                name: draft.name,
                tablesCount: draft.tables.length,
                totalSeats: draft.tables.reduce((sum, t) => sum + t.seats, 0),
                status: draft.isActive ? 'Active' : 'Inactive',
                isActive: draft.isActive,
                createdAt: updatedAt,
                createdBy,
            });
            setStepIndex(5);
        } catch (err) {
            showSaveError(err, i18n.t('tableMaps.wizard.review.saveError'));
        } finally {
            setSaving(false);
        }
    }

    function renderStepPanel() {
        if (stepIndex === 0) {
            return <BasicInformationStep />;
        }
        if (stepIndex === 1) {
            return <MapConfigurationStep />;
        }
        if (stepIndex === 2) {
            return <TablePlacementStep />;
        }
        if (stepIndex === 3) {
            return <TableDetailsStep originalTables={originalDetails?.tables ?? []} />;
        }
        if (stepIndex === 4) {
            return <ReviewStep />;
        }
        if (stepIndex === 5 && createdResult) {
            return <CreatedStep draft={draft} createdResult={createdResult} isEdit={isEdit} />;
        }
        return null;
    }

    const title = isEdit ? i18n.t('tableMaps.wizard.editTitle') : i18n.t('tableMaps.wizard.title');

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <div className={classes.headerTopRow}>
                    <div>
                        <Typography className={classes.headerTitle}>{title}</Typography>
                        <Typography className={classes.headerSubtitle}>
                            {i18n.t('tableMaps.wizard.stepOf', { n: currentStep })}
                        </Typography>
                    </div>
                    <div className={classes.headerActions}>
                        {stepIndex === 5 ? (
                            <>
                                <Button
                                    variant="outlined"
                                    className={classes.backButton}
                                    onClick={() => history.push('/table-maps')}
                                >
                                    {i18n.t('tableMaps.wizard.created.backToTableMaps')}
                                </Button>
                                <Button
                                    variant="contained"
                                    className={classes.nextButton}
                                    disabled={!createdResult}
                                    onClick={() => {
                                        if (createdResult) {
                                            history.push(`/table-maps/${createdResult.id}`);
                                        }
                                    }}
                                >
                                    {i18n.t('tableMaps.wizard.created.openTableMap')}
                                </Button>
                            </>
                        ) : (
                            <>
                                {stepIndex > 0 && (
                                    <Button
                                        variant="outlined"
                                        className={classes.backButton}
                                        onClick={handleBack}
                                    >
                                        {i18n.t('tableMaps.wizard.back')}
                                    </Button>
                                )}
                                <Button
                                    variant="outlined"
                                    className={classes.cancelButton}
                                    onClick={handleCancel}
                                >
                                    {i18n.t('tableMaps.wizard.cancel')}
                                </Button>
                                {stepIndex < 4 && (
                                    <Button
                                        variant="contained"
                                        className={classes.nextButton}
                                        onClick={handleNext}
                                        disabled={!canAdvance}
                                    >
                                        {i18n.t('tableMaps.wizard.next')}
                                    </Button>
                                )}
                                {stepIndex === 4 && (
                                    <Button
                                        variant="contained"
                                        className={classes.nextButton}
                                        onClick={isEdit ? handleUpdate : handleCreate}
                                        disabled={!isReviewReady(draft) || saving}
                                    >
                                        {isEdit
                                            ? i18n.t('tableMaps.wizard.saveChanges')
                                            : i18n.t('tableMaps.wizard.review.createTableMap')}
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <WizardStepper
                    currentStep={currentStep}
                    onStepClick={
                        isEdit && stepIndex !== 5 ? (target) => setStepIndex(target) : undefined
                    }
                />
            </div>

            <div className={classes.body}>
                <div>{renderStepPanel()}</div>
            </div>

            <PopupConfirmation
                open={cancelConfirmOpen}
                close={() => setCancelConfirmOpen(false)}
                title={
                    isEdit
                        ? i18n.t('tableMaps.wizard.cancelEditTitle')
                        : i18n.t('tableMaps.wizard.cancelTitle')
                }
                description={i18n.t('tableMaps.wizard.cancelDescription')}
                activeBtn={i18n.t('tableMaps.wizard.cancelConfirm')}
                action={handleCancelConfirmed}
            />
        </div>
    );
}

export default function CreateTableMapWizard() {
    const { id } = useParams<{ id?: string }>();
    const dispatch = useDispatch();
    const history = useHistory();
    const details = useSelector((state: ApplicationState) =>
        id ? state.tableMaps.details[id] : undefined
    );
    const [fetchDone, setFetchDone] = useState<boolean>(!id);

    useEffect(() => {
        if (id) {
            const thunk = tableMapsStore.actionCreators.getTableMapDetails(id);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (dispatch(thunk as any) as Promise<void>).then(() => setFetchDone(true));
        }
    }, [id, dispatch]);

    if (id) {
        if (!fetchDone) {
            return (
                <Box
                    display="flex"
                    height="100%"
                    alignItems="center"
                    justifyContent="center"
                    bgcolor={theme.palette.common.white}
                >
                    <CircularProgress />
                </Box>
            );
        }

        if (!details) {
            history.push('/table-maps');
            return null;
        }

        const prefillDraft: TableMapDraft = {
            name: details.name,
            isActive: details.isActive,
            gridEnabled: details.gridEnabled,
            entrance: details.entrance,
            size: details.size,
            area: details.area,
            tableDefaults: details.tableDefaults,
            tables: details.tables,
        };

        return (
            <TableMapWizardProvider initialDraft={prefillDraft}>
                <WizardShell isEdit={true} editId={id} originalDetails={details} />
            </TableMapWizardProvider>
        );
    }

    return (
        <TableMapWizardProvider>
            <WizardShell isEdit={false} />
        </TableMapWizardProvider>
    );
}
